import React, { HTMLProps } from 'react'
import { AxiosRequestConfig } from 'axios'
import { SelectType } from 'tennders-common/types/SelectType'
import useAutocomplete from 'tennders-common/form/hooks/useAutocomplete'
import Suggestions from 'tennders-common/form/components/new-inputs/Suggestions'
import Trans from 'next-translate/Trans'
import ReactTooltip from 'react-tooltip'
import { isEmpty } from 'lodash'
import { clearInput } from 'tennders-stracture/libs/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import stylesInput from '@/styles/Input.module.scss'
import ClearInputButton from '../ClearInputButton'

type Props = Omit<HTMLProps<HTMLInputElement>, 'onSet' | 'onTextSet' | 'value' | 'name'> & {
  name?: string,
  value ?: any,
  initialValue ?: any,
  onSet ?: (value: any) => void,
  url ?: string,
  items ?: SelectType[],
  error ?: string,
  className ?: string,
  required ?: boolean,
  readOnly ?: boolean,
  suggestionStyle ?: React.CSSProperties,
  showError ?: boolean,
  requestBuilder ?: ({ keyword, options }: {keyword: string, options: AxiosRequestConfig}) => Promise<any>,
  dataExporter ?: (data: Record<string, any>) => Record<string, any>,
  clearInputButton ?: JSX.Element,
  customOnChange ?: (value: string) => void,
  customValue ?: string,
  showSuggestions ?: boolean
}

const AutoComplete = (props : Props) => {
  const {
    value,
    initialValue,
    onSet,
    url,
    requestBuilder: propsRequestBuilder,
    items,
    onClick,
    error,
    className,
    dataExporter,
    readOnly,
    suggestionStyle = {},
    showError,
    clearInputButton,
    customOnChange,
    customValue,
    showSuggestions = true,
    ...rest
  } = props

  const {
    text,
    suggestions,
    setSuggestions,
    onTextChanged,
    suggestionsLoading,
    handleSuggestionSelect,
    showNoDataText
  } = useAutocomplete({
    value,
    customValue,
    dataExtractor: dataExporter,
    url,
    requestBuilder: propsRequestBuilder,
    items,
    onSet,
    initialValue
  })

  return (
    <div className="pos-rel">
      <input
        onClick={onClick}
        readOnly={!!readOnly}
        disabled={!!readOnly}
        className={`${className || 'form-input w-100'}${error ? ' error' : ''}${readOnly ? ' disabled' : ''}`}
        onChange={(e) => {
          if (readOnly) {
            return
          }
          onTextChanged(e)
          if (customOnChange) {
            customOnChange(e?.target?.value)
          }
        }}
        type="text"
        autoComplete="new-password"
        {...rest}
        name={'autocomplete_' + Date.now()}
        value={text}
      />
      {suggestions?.length > 0 && showSuggestions
        ? (
          <Suggestions
            onOut={null}
            handleSuggestionSelect={handleSuggestionSelect}
            suggestions={suggestions}
            suggestionsLoading={suggestionsLoading}
            style={suggestionStyle}
          />
        )
        : (
          showNoDataText && (
            <div className="d-flex pos-rel">
              <ul style={suggestionStyle} className={stylesInput['autocomplete-ul']}>
                <li>
                  <span><Trans i18nKey="variables:no_items_found" /></span>
                </li>
              </ul>
            </div>
          )
        )}
      {showError && error && (
        <span className="d-block mt-10">
          <FontAwesomeIcon icon={faCircleExclamation} data-tip data-for={error} size="lg" className="color-red" />
          <ReactTooltip id={error} type="error">
            {error}
          </ReactTooltip>
        </span>
      )}
      {clearInputButton || <ClearInputButton clearInput={() => clearInput(onSet, text, () => onTextChanged({ target: { value: '' } }))} showClearInput={!isEmpty(text) && !readOnly} />}
    </div>
  )
}

export default React.memo(AutoComplete)
